import FilterItem from 'components/FilterItem';
import AppStoreContext, { AppStore } from 'contexts/AppStoreContext';
import Immutable from 'immutable';
import React, { useState, useContext } from 'react';
import { SubmittalCategory } from 'records/SubmittalCategory';
import { SubmittalQuery } from 'records/SubmittalQuery';
import { SubmittalStatus } from 'records/SubmittalStatus';
import '../modals.css';
import { useTranslator } from '@bluebeam/react-i18n';

type SubmittalQueryFields = 'statuses' | 'categories';

const SubmittalListFilterModal = () => {
  const { state, actions } = useContext(AppStoreContext);
  const { submittalStatuses, submittalCategories } = state;
  const [submittalQuery, setsubmittalQuery] = useState<SubmittalQuery>(
    state.submittalQuery
  );

  const { translate } = useTranslator([
    'submittalsPage',
    'filter',
    'statusLabels',
    'buttons',
  ]);

  const handleCategoriesChange = (
    groupName: SubmittalQueryFields,
    category: SubmittalCategory,
    checked: boolean
  ): void => {
    setsubmittalQuery(
      submittalQuery?.update(
        groupName,
        (
          d: Immutable.Set<SubmittalCategory>
        ): Immutable.Set<SubmittalCategory> => {
          return checked ? d.add(category) : d.delete(category);
        }
      )
    );
  };

  const handleStatusesChange = (
    groupName: SubmittalQueryFields,
    status: SubmittalStatus,
    checked: boolean
  ): void => {
    setsubmittalQuery(
      submittalQuery?.update(
        groupName,
        (d: Immutable.Set<SubmittalStatus>): Immutable.Set<SubmittalStatus> => {
          return checked ? d.add(status) : d.delete(status);
        }
      )
    );
  };

  const handleApplyFilters = (): void => {
    actions.setSubmittalQuery(submittalQuery);
    actions.querySubmittals();
    actions.clearAllModals();
  };

  const handleResetStatuses = (): void => {
    const statuses = submittalStatuses.reduce(
      (acc, a) => acc.add(a),
      Immutable.OrderedSet()
    );

    const nextSubmittalQuery = submittalQuery.set('statuses', statuses);
    setsubmittalQuery(nextSubmittalQuery);
  };

  const handleResetCategories = (): void => {
    const categories = submittalCategories.reduce(
      (acc, c) => acc.add(c),
      Immutable.OrderedSet()
    );

    const nextSubmittalQuery = submittalQuery.set('categories', categories);
    setsubmittalQuery(nextSubmittalQuery);
  };

  const handleOnlyStatusClick = (val: SubmittalStatus): void => {
    const updatedSubmittalQuery = submittalQuery.update(
      'statuses',
      (): Immutable.Set<SubmittalStatus> =>
        Immutable.Set<SubmittalStatus>([val])
    );

    setsubmittalQuery(updatedSubmittalQuery);
  };

  const handleOnlyCategoryClick = (val: SubmittalCategory): void => {
    const updatedSubmittalQuery = submittalQuery.update(
      'categories',
      (): Immutable.Set<SubmittalCategory> =>
        Immutable.Set<SubmittalCategory>([val])
    );
    setsubmittalQuery(updatedSubmittalQuery);
  };

  const handleCloseModal = (): void => {
    actions.clearAllModals();
  };

  const translatedCategories = submittalCategories.map((category) => {
    const categoryType: `${string}:${string}` =
      `submittalsPage:categories.${category.kind}` as `${string}:${string}`;

    return new SubmittalCategory({
      kind: category.kind,
      display: translate(categoryType),
    });
  });

  const translatedStatuses = submittalStatuses.map((status) => {
    const categoryType: `${string}:${string}` =
      `statusLabels:submittals.${status.kind}` as `${string}:${string}`;

    return new SubmittalStatus({
      kind: status.kind,
      display: translate(categoryType),
    });
  });

  return (
    <div id="sub-list-filter-modal">
      <div>
        <div className="column">
          <div className="bgd-gray-2 hs3 row items-center justify-between px3">
            <div className="font-size-6">
              {translate('filters:filterItems')}
            </div>
            <span
              id="sub-list-filter-modal-close-x"
              className="flex-center line-height-1 font-size-9 cursor-pointer"
              onClick={handleCloseModal}
            >
              &times;
            </span>
          </div>
          <div className="p3 row justify-around">
            <div>
              <div className="row items-center">
                <div className=" font-size-7 mr4">
                  {translate('statusLabels:statuses.title')}
                </div>
                <span
                  id="sub-list-filter-modal-reset-statuses"
                  onClick={handleResetStatuses}
                  className="font-size-3 underline cursor-pointer"
                >
                  {translate('statusLabels:resetStatuses')}
                </span>
              </div>
              {translatedStatuses
                .map(
                  (a: SubmittalStatus): React.ReactNode => (
                    <FilterItem
                      id="sub-list-filter-modal-filter-item-sub-statuses"
                      key={a.kind}
                      text={a.display}
                      only
                      checked={submittalQuery.statuses.has(a)}
                      onChange={(checked: boolean): void =>
                        handleStatusesChange('statuses', a, checked)
                      }
                      onOnly={(): void => handleOnlyStatusClick(a)}
                    />
                  )
                )
                .toArray()}
            </div>
            <div>
              <div className="row items-center">
                <div className=" font-size-7 mr4">
                  {translate('submittalsPage:categories.title')}
                </div>
                <span
                  id="sub-list-filter-modal-filter-item-reset-categories"
                  onClick={handleResetCategories}
                  className="font-size-3 underline cursor-pointer"
                >
                  {translate('submittalsPage:resetCategories')}
                </span>
              </div>
              {translatedCategories
                .map(
                  (c: SubmittalCategory): React.ReactNode => (
                    <FilterItem
                      id="sub-list-filter-modal-filter-item-categories"
                      key={c.kind}
                      text={c.display}
                      only
                      checked={submittalQuery.categories.has(c)}
                      onChange={(checked: boolean): void =>
                        handleCategoriesChange('categories', c, checked)
                      }
                      onOnly={(): void => handleOnlyCategoryClick(c)}
                    />
                  )
                )
                .toArray()}
            </div>
          </div>
        </div>
      </div>

      <div className="row justify-end p3">
        <button
          id="sub-list-filter-modal-apply-btn"
          className="btn-lg btn-primary"
          onClick={handleApplyFilters}
        >
          {translate('buttons:apply')}
        </button>
      </div>
    </div>
  );
};

SubmittalListFilterModal.modalClasses = {
  overlay: `modal__overlay`,
  content: `modal__window`,
};

SubmittalListFilterModal.modalStyles = {
  content: {
    width: '900px',
  },
};

export default SubmittalListFilterModal;
